import { spawn } from "node:child_process";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../lib/m8t-command.js";
import { resolveGatewayContext } from "../lib/gateway-context.js";
import {
  OPEN_TARGETS,
  type OpenTarget,
  type GatewayLocator,
  resolveOpenUrl,
} from "../lib/open-targets.js";
import { LocalCliError } from "../lib/errors.js";
import { renderJson, resolveOutputMode } from "../lib/output.js";

/** Open a URL in the OS default browser (best-effort, detached). The URL is
 *  always printed too, so a failure here just means the user opens it manually. */
function openUrl(url: string): void {
  const [cmd, args] =
    process.platform === "darwin"
      ? ["open", [url]]
      : process.platform === "win32"
        ? ["cmd", ["/c", "start", "", url]]
        : ["xdg-open", [url]];
  const child = spawn(cmd, args, { stdio: "ignore", detached: true });
  child.on("error", () => {
    /* swallow — the URL was printed; opening is best-effort */
  });
  child.unref();
}

export class OpenCommand extends M8tCommand {
  static paths = [["open"]];
  static usage = Command.Usage({
    description: "Open the deployed webapp (default), the Foundry portal, or the resource group.",
    details:
      "Targets: webapp (deployed app, default) | foundry (ai.azure.com) | portal (resource group in the Azure portal). Pass --print to emit the URL instead of launching a browser (also the default when stdout isn't a TTY).",
  });

  target = Option.String({ required: false });
  print = Option.Boolean("--print", false);
  output = Option.String("--output");
  resourceGroup = Option.String("--resource-group", {
    description: "m8t resource group to disambiguate the gateway (multi-deployment subscriptions).",
  });

  async executeCommand(): Promise<number> {
    const mode = resolveOutputMode(
      this.output as "pretty" | "json" | "auto" | undefined,
      this.context.stdout as NodeJS.WriteStream,
    );
    const target = (this.target ?? "webapp");
    if (!(OPEN_TARGETS as readonly string[]).includes(target)) {
      throw new LocalCliError({
        code: "OPEN_UNKNOWN_TARGET",
        message: `Unknown target '${target}'.`,
        hint: `Valid targets: ${OPEN_TARGETS.join(", ")}.`,
      });
    }

    let gw: GatewayLocator | null = null;
    if (target !== "foundry") {
      const ctx = await resolveGatewayContext({ interactive: false, resourceGroup: this.resourceGroup });
      gw = {
        gatewayUrl: ctx.gatewayUrl,
        gatewayTenantId: ctx.gatewayTenantId,
        subscriptionId: ctx.subscriptionId,
        containerAppResourceId: ctx.containerAppResourceId,
      };
    }
    const url = resolveOpenUrl(target as OpenTarget, gw);

    // --print always emits the bare URL (pipe-friendly), winning over the
    // non-TTY auto-JSON default; explicit `--output json` (without --print)
    // still emits JSON for scripting.
    if (mode === "json" && !this.print) {
      this.context.stdout.write(renderJson({ target, url }) + "\n");
      return 0;
    }

    const isTTY = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
    if (this.print || !isTTY) {
      this.context.stdout.write(url + "\n");
      return 0;
    }
    this.context.stdout.write(`opening ${target}: ${url}\n`);
    openUrl(url);
    return 0;
  }
}
